import React, { useMemo, useCallback } from 'react';
import type { DocNode } from '@atlaskit/adf-schema';
import { useAnnouncementBannerSettingsV4Resource } from '@atlassian/jira-admin-pages-system-ui-announcement-banner-resources/src/controllers/settings/resource/index.tsx';
import { useUserDismissActions } from '@atlassian/jira-admin-pages-system-ui-announcement-banner-resources/src/controllers/user-dismiss/index.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	BANNER,
} from '@atlassian/jira-product-analytics-bridge';
import {
	BACKGROUND_THEMES,
	GREY_META,
} from '@atlassian/jira-system-announcement-banner-main/src/ui/settings/form/background-picker/constants.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { Announcement } from './announcement/index.tsx';
import { Content } from './content/index.tsx';

export const BannerAnnouncement = () => {
	const { data, update } = useAnnouncementBannerSettingsV4Resource();

	const analyticsAttributes = useMemo(
		() => ({
			visibility: data?.visibility,
			isDismissable: data?.isDismissable,
		}),
		[data?.isDismissable, data?.visibility],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [, { set: dismiss }] = useUserDismissActions();
	const accountId = useAccountId();
	const onDismiss = useCallback(() => {
		if (accountId != null && data?.hashId != null) {
			update((currentData) => (currentData ? { ...currentData, isDismissed: true } : currentData));
			dismiss(accountId, data?.hashId);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'closeAnnouncementBanner',
			);
		}
	}, [accountId, createAnalyticsEvent, data?.hashId, dismiss, update]);

	// Fallback on grey background theme if no colour found
	const backgroundTheme =
		BACKGROUND_THEMES.find((theme) => theme.name === data?.bgColor) ?? GREY_META;

	return data ? (
		<ContextualAnalyticsData sourceName="announcement" sourceType={BANNER}>
			<Announcement
				isDismissable={Boolean(data?.isDismissable)}
				onDismiss={onDismiss}
				backgroundTheme={backgroundTheme}
			>
				{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
				<Content content={JSON.parse(data.content) as DocNode} />
			</Announcement>
			<FireScreenAnalytics attributes={analyticsAttributes} />
		</ContextualAnalyticsData>
	) : null;
};
